export { PropertiesValidation as default };

/**
* Properties validation
*
* Validates an object and all its properties according to set rules
*
* @author      rsimoes <ruben-r-simoes@telecom.pt>
* @copyright   Copyright (C) Sapo. All rights reserved.
* @license     Private
* @since       7.0.0
*/
class PropertiesValidation {
  /**
   * Defines common regex rules
   *
   * @private
   *
   * @type {Object}
   *
   * @since 7.0.0
   */
  static REGEX_RULES = {
    email        : new RegExp(/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/),
    phone        : new RegExp(/^\d{6,16}$/),
    birth_date   : new RegExp(/^\d{4}-(0[1-9]|1[012])-(0[1-9]|[12][0-9]|3[01])$/),
    date         : new RegExp(/^\d{4}-\d{2}-\d{2}$/),
    alphanumeric : new RegExp(/^[a-zA-Z0-9]+$/),
    domain       : new RegExp(/^(?!:\/\/)([a-zA-Z0-9-_]+\.)*[a-zA-Z0-9][a-zA-Z0-9-_]+\.[a-zA-Z]{2,6}?$/),
  }

  /**
   * Defines the lists of invalid field names
   *
   * @public
   *
   * @type {Object}
   *
   * @since 7.0.0
   */
  invalid = {};

  /**
   * Defines the number of invalid fields
   *
   * @public
   *
   * @type {Object}
   *
   * @since 7.0.0
   */
  invalid_count = 0;

  /**
   * Defines the set of rules to be applied on all values
   *
   * @private
   *
   * @type {Object} { "field" : { required: true/false, regex: "regex_name"}}
   *
   * @since 7.0.0
   */
  rules;

  /**
   * Defines custom error messages for fields
   *
   * @public
   *
   * @type {Object} { "field" : "custom error message"}
   *
   * @since 7.0.0
   */
  custom_errors = {};

  /**
   * Constructor
   *
   * @public
   *
   * @param {Object} rules { "field" : { required: true/false, regex: "regex_name", regex_custom: "custom regex name"}}
   *
   * @since 7.0.0
   */
  constructor(rules = {}) {
    this.setRules(rules);
  }

  /**
   * Sets validation rules to be used
   *
   * @public
   *
   * @param {Object} rules { "field" : { required: true/false, regex: "regex_name", regex_custom: "custom regex name"}}
   *
   * @since 7.0.0
   */
  setRules(rules) {
    this.rules = rules;
  }

  /**
   * Applies all set rules to the values of a given object
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {Object} data  Object with the values to be validated, object must contain the field name as key in order to be validated
   *
   * @return {Boolean}
   */
  validateObject(data) {
    this.reset();

    for (let field in this.rules) {
      this.validateField(field, data[field] || null);
    }

    return this.invalid_count > 0 ? false : true;
  }

  /**
   * Sets a custom error message for a given field
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} field   Field name
   * @param {String} message Custom error message
   */
  setCustomError(field, message) {
    this.custom_errors[field] = message;
  }

  /**
   * Validates a single field based on the set rule
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String}                  field Field name
   * @param {String|Number|undefined} value Field value
   *
   * @return {Boolean}
   */
  validateField(field, value = null) {
    let rule = this.rules[field];

    if (rule.min_length && value && this.validateMinLength(field, value, rule.min_length) === false) {
      return false;
    }

    if (rule.max_length && value && this.validateMaxLength(field, value, rule.max_length) === false) {
      return false;
    }

    if (rule.regex_custom && value) {
      return this.validateRegexCustom(field, value, rule.regex_custom);
    }

    if (rule.regex && value) {
      return this.validateRegex(field, value, rule.regex);
    }

    if (rule.required) {
      return this.validateRequired(field, value);
    }

    return true
  }

  /**
   * Validates if a given field as a valida value
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {String}                  field Field name
   * @param {String|Number|undefined} value Field value
   *
   * @return {Boolean}
   */
  validateRequired(field, value) {
    if (!value) {
      this.setInvalid(field);
      return false;
    }

    this.setValid(field);
    return true;
  }

  /**
   * Validates if a given field meets the minimum length requirement
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {String}                  field      Field name
   * @param {String|Number|undefined} value      Field value
   * @param {Number}                  min_length Minimum length required
   *
   * @return {Boolean}
   */
  validateMinLength(field, value, min_length) {
    if (value.length < min_length) {
      this.setInvalid(field);
      return false;
    }

    this.setValid(field);
    return true;
  }

  /**
   * Validates if a given field meets the maximum length requirement
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {String}                  field      Field name
   * @param {String|Number|undefined} value      Field value
   * @param {Number}                  max_length Maximum length required
   *
   * @return {Boolean}
   */
  validateMaxLength(field, value, max_length) {
    if (value.length > max_length) {
      this.setInvalid(field);
      return false;
    }

    this.setValid(field);
    return true;
  }

  /**
   * Validates a given value according to a custom regex
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {String}                  field      Field name
   * @param {String|Number|undefined} value      Field value
   * @param {String}                  expression Regex expression
   *
   * @return {Boolean}
   */
  validateRegexCustom(field, value, expression) {
    if (!value.match(new RegExp(expression))) {
      this.setInvalid(field);
      return false;
    }

    this.setValid(field);
    return true;
  }

  /**
   * Validates a given value according to a defined regex
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {String}                  field      Field name
   * @param {String|Number|undefined} value      Field value
   * @param {String}                  regex_name Regex name
   *
   * @return {Boolean}
   */
  validateRegex(field, value, regex_name) {
    if (!value.match(PropertiesValidation.REGEX_RULES[regex_name])) {
      this.setInvalid(field);
      return false;
    }

    this.setValid(field);
    return true;
  }

  /**
   * Defines a field as invalid
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {String}      field field name
   * @param {String|null} custom_error custom error message
   *
   * @return {Boolean}
   */
  setInvalid(field, custom_error = null) {
    if (custom_error) {
      this.custom_errors[field] = custom_error;
    }

    if (this.invalid[field]) {
      return;
    }

    this.invalid[field] = true;
    this.invalid_count++;
  }

  /**
   * Defines a field as valid
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {String} field field name
   *
   * @return {Boolean}
   */
   setValid(field) {
    if (!this.invalid[field]) {
      return;
    }

    delete this.invalid[field];
    this.invalid_count--;
  }

  /**
   * Resets all the validation variables to it's initial state
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {Boolean}
   */
  reset() {
    this.invalid_count  = 0;
    this.invalid        = {};
    this.custom_errors  = {};
  }

  /**
   * Returns the first invalid field name
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {String}
   */
  getFirstInvalid() {
    return Object.keys(this.invalid)[0]
  }
}